import { useMemo, useState } from 'react';
import { Pencil, Plus } from 'lucide-react';
import { Btn, LoadError, LoadSkeleton, PublicationStatus, SectionHeading } from '../components/ui';
import AdminVaultPackEditor from '../components/admin/AdminVaultPackEditor';
import { useAdminVault } from '../hooks';
import { c } from '../styles/theme';

export default function AdminVault() {
  const { packs, load, loadPacks, createPack, updatePack, deletePack, pending, isPending } = useAdminVault();
  const [panelMode, setPanelMode] = useState<'create' | 'edit' | null>(null);
  const [selectedId, setSelectedId] = useState('');

  const selectedPack = useMemo(
    () => packs.find((pack) => pack.id === selectedId) ?? null,
    [packs, selectedId]
  );
  const collectOptions = useMemo(() => {
    const values = {
      categories: new Set<string>(),
      genres: new Set<string>(),
      moods: new Set<string>(),
      difficulties: new Set<string>(),
      platforms: new Set<string>(),
    };

    const add = (value: string | undefined) => {
      if (!value || !value.trim()) return '';
      return value.trim();
    };

    for (const pack of packs) {
      const category = add(pack.category);
      if (category) values.categories.add(category);

      for (const prompt of pack.prompts) {
        const genre = add(prompt.genre);
        const mood = add(prompt.mood);
        const difficulty = add(prompt.difficulty);
        if (genre) values.genres.add(genre);
        if (mood) values.moods.add(mood);
        if (difficulty) values.difficulties.add(difficulty);
      }

      for (const preset of pack.presets) {
        const mood = add(preset.mood);
        if (mood) values.moods.add(mood);
      }

      for (const hook of pack.hooks) {
        const genre = add(hook.genre);
        if (genre) values.genres.add(genre);
      }

      for (const item of [...pack.metadata, ...pack.marketing]) {
        const platform = add(item.platform);
        if (platform) values.platforms.add(platform);
      }
    }

    return {
      categories: [...values.categories],
      genres: [...values.genres],
      moods: [...values.moods],
      difficulties: [...values.difficulties],
      platforms: [...values.platforms],
    };
  }, [packs]);
  const categories = collectOptions.categories;

  if (load.status === 'loading' && packs.length === 0) {
    return <LoadSkeleton rows={3} rowHeight={108} label="Loading vault packs…" />;
  }

  if (load.status === 'error' && packs.length === 0) {
    return <LoadError message={load.error?.message ?? 'Could not load vault packs.'} onRetry={loadPacks} />;
  }

  return (
    <div>
      <SectionHeading
        as="h2"
        title="Vault"
        subtitle="Admin CRUD for DFY Producer Vault packs"
        marginBottom={14}
      />

      <div
        style={{
          background: c.surface1,
          border: `1px solid ${c.border}`,
          borderRadius: 12,
          padding: '14px 16px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 10,
          }}
        >
          <div>
            <p style={{ fontSize: 12, color: c.text2, margin: '0 0 4px' }}>Available packs</p>
            <p style={{ fontSize: 11, color: c.text3, margin: 0 }}>
              Select a pack to edit it in the side panel.
            </p>
          </div>
          <Btn
            onClick={() => {
              setSelectedId('');
              setPanelMode('create');
            }}
            disabled={pending}
          >
            <Plus size={13} />
            Create pack
          </Btn>
        </div>

        {packs.length === 0 ? (
          <p style={{ fontSize: 12, color: c.text3, margin: 0 }}>No persisted packs yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {packs.map((pack) => (
              <button
                key={pack.id}
                type="button"
                onClick={() => {
                  setSelectedId(pack.id);
                  setPanelMode('edit');
                }}
                style={{
                  textAlign: 'left',
                  border: `1px solid ${selectedId === pack.id && panelMode === 'edit' ? c.accentBorder : c.border}`,
                  borderRadius: 10,
                  padding: '10px 12px',
                  background: selectedId === pack.id && panelMode === 'edit' ? c.accentSoft : c.surface2,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 500, margin: '0 0 3px', color: c.accent }}>{pack.name}</p>
                  <p style={{ fontSize: 11, color: c.text3, margin: 0 }}>{pack.category} · v{pack.version}</p>
                </div>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: c.text2, fontSize: 11 }}>
                  <PublicationStatus published={pack.published !== false} />
                  <Pencil size={12} />
                  Edit
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {panelMode && (
        <AdminVaultPackEditor
          mode={panelMode}
          pack={panelMode === 'edit' ? selectedPack : null}
          categories={categories}
          fieldOptions={collectOptions}
          pending={pending || (selectedPack ? isPending(selectedPack.id) : false)}
          onClose={() => setPanelMode(null)}
          onSave={(pack) =>
            panelMode === 'create'
              ? createPack(pack)
              : updatePack(selectedPack?.id ?? pack.id, pack)
          }
          onDelete={
            panelMode === 'edit' && selectedPack
              ? async (id) => {
                  await deletePack(id);
                  setSelectedId('');
                }
              : undefined
          }
        />
      )}
    </div>
  );
}
